import { useEffect, useRef, useState } from "react";
import { Users, Radio, UserPlus } from "lucide-react";

const useCountUp = (target) => {
  const [n, setN] = useState(0);
  const from = useRef(0);
  useEffect(() => {
    if (target == null) return undefined;
    const start = from.current;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 1200);
      setN(Math.round(start + (target - start) * (1 - (1 - p) ** 3)));
      if (p < 1) raf = requestAnimationFrame(step);
      else from.current = target;
    };
    raf = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [target]);
  return target == null ? null : n;
};

const ago = (iso) => {
  const s = Math.max(1, Math.round((Date.now() - new Date(iso)) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

const Stat = ({ icon: Icon, label, value, hint, testId }) => (
  <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-6">
    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-400 text-slate-900"><Icon className="h-6 w-6" /></span>
    <div>
      <p className="font-mono-num text-3xl font-semibold text-white" data-testid={testId}>{value ?? "—"}</p>
      <p className="text-xs font-bold uppercase tracking-widest text-blue-200">{label}</p>
      {hint && <p className="mt-1 text-xs text-emerald-300">{hint}</p>}
    </div>
  </div>
);

export const LiveStats = ({ stats }) => {
  const recent = stats?.recent_signups || [];
  const passengers = useCountUp(stats?.passengers);
  const online = useCountUp(stats?.online_drivers);
  return (
    <section data-testid="live-stats-section" className="relative bg-[#0E2240] px-4 py-14 sm:px-6 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-amber-400">
          <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
          Live on Comfort Taxi
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Stat icon={Users} label="Passengers registered" value={passengers?.toLocaleString()} hint={stats?.new_today ? `+${stats.new_today} in the last 24 hours` : null} testId="live-stat-passengers" />
          <Stat icon={Radio} label="Taxis online now" value={online} testId="live-stat-online" />
        </div>
        {recent.length > 0 && (
          <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-white/5" data-testid="recent-signups-ticker">
            <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3 text-sm text-blue-100">
              <UserPlus className="h-4 w-4 text-amber-400" />
              <b className="text-white">{stats.new_today}</b> joined in the last 24 hours
            </div>
            <div className="ct-marquee flex gap-3 whitespace-nowrap px-5 py-3">
              {[...recent, ...recent].map((u, i) => (
                <span key={i} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-white">
                  <span className={`h-2 w-2 rounded-full ${u.role === "driver" ? "bg-amber-400" : "bg-emerald-400"}`} />
                  <b>{u.name}</b> joined as {u.role === "driver" ? "a driver" : "a passenger"}
                  <span className="text-xs text-blue-200">· {ago(u.at)}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
